// OAuth 시작 함수 (Step 8.3)
// - Google: Supabase OAuth provider 직접 사용
// - Naver/Kakao: 웹의 /api/auth/*/start 라우트로 위임하고 실행 플랫폼을 from으로 전달
// - 둘 다 시스템 브라우저(@capacitor/browser) 사용 → Apple guideline 4.5/Google 임베디드 차단 회피

import { Browser } from "@capacitor/browser";
import { Capacitor } from "@capacitor/core";
import { Preferences } from "@capacitor/preferences";
import { supabase } from "./supabase";
import { apiFetch } from "./api";

// AndroidManifest intent-filter scheme과 동일해야 함. 네이티브 경로는 변경하지 않는다.
export const OAUTH_REDIRECT_URI = "com.sotda.app://auth/callback";

/**
 * 웹 OAuth 콜백. 배포 환경값이 없으면 현재 SPA origin을 써 로컬 dev 왕복도 허용한다.
 *
 * ⚠ 경로가 두 갈래다 (설계 docs/specs/2026-07-28-spa-root-domain-design.md §4-1).
 *   SPA가 sotda.com 루트로 올라가면 /auth/callback은 Next의 Google PKCE 핸들러가 이미 점유하고
 *   있어 SPA 페이지에 도달하지 못한다. 그래서 통합 도메인에서만 /auth/spa-callback을 쓴다.
 *   빌드 플래그가 아니라 실행 중 호스트로 판단하므로 두 배포가 같은 번들을 공유할 수 있다.
 */
export function getWebOAuthRedirectUri(origin?: string): string {
  const configured = (import.meta.env.VITE_WEB_OAUTH_REDIRECT as string | undefined)?.trim();
  if (configured) return configured;
  const currentOrigin = origin ?? (typeof window !== "undefined" ? window.location.origin : "");
  const base = currentOrigin.replace(/\/$/, "");
  const isUnifiedDomain = /^https:\/\/sotda\.com$/.test(base);
  return `${base}${isUnifiedDomain ? "/auth/spa-callback" : "/auth/callback"}`;
}

/** OAuth 시작 URL에서 로그에 남겨도 되는 부분만 추린다 — redirect_to·code_challenge_method. 토큰·verifier 제외. */
export function describeOAuthUrl(url: string): { redirect_to: string | null; code_challenge_method: string | null } {
  try {
    const u = new URL(url);
    return { redirect_to: u.searchParams.get("redirect_to"), code_challenge_method: u.searchParams.get("code_challenge_method") };
  } catch {
    return { redirect_to: null, code_challenge_method: null };
  }
}

export function getOAuthRedirectUri(origin?: string): string {
  return Capacitor.isNativePlatform() ? OAUTH_REDIRECT_URI : getWebOAuthRedirectUri(origin);
}

// 구글 계정연동(linkIdentity) 진행 중 플래그 — 콜백이 로그인과 같은 URL로 돌아오므로
// deeplink.ts가 "이건 로그인이 아니라 연동"임을 구분하도록 임시로 남긴다 (5분 내 유효).
export const PENDING_ID_LINK_KEY = "sotda_pending_id_link";

const API_BASE = (import.meta.env.VITE_API_BASE as string) || "https://sotda.com";

/**
 * Google OAuth 시작. Supabase가 redirect_uri를 OAuth flow에 주입.
 * 흐름: 앱 → 시스템 브라우저(Google 동의화면) → Supabase callback → deep link → deeplink.ts 캐치
 */
export async function startGoogleOAuth(): Promise<void> {
  const redirectTo = getOAuthRedirectUri();
  const { data, error } = await supabase.auth.signInWithOAuth({
    provider: "google",
    options: {
      redirectTo,
      // 브라우저 자동 리다이렉트 X — 우리가 직접 시스템 브라우저로 열어야 함
      skipBrowserRedirect: true,
    },
  });
  if (error) throw new Error(`Google OAuth 시작 실패: ${error.message}`);
  if (!data?.url) throw new Error("Google OAuth URL을 받지 못했습니다.");

  // 디버그 — redirect_to·PKCE 방식만 남긴다. URL 전체를 찍지 않는 이유: WebCrypto가 없는
  // 환경에서 SDK가 code_challenge_method=plain으로 내려가면 verifier가 URL에 그대로 실린다
  // (2026-09-14 조사 §B-2). 로그에 남으면 안 되는 값이다.
  console.log("[oauth] Google signInWithOAuth", describeOAuthUrl(data.url));

  if (Capacitor.isNativePlatform()) {
    await Browser.open({ url: data.url, presentationStyle: "popover" });
  } else {
    // 웹 dev 환경 — 같은 탭으로 이동
    window.location.href = data.url;
  }
}

/**
 * Apple OAuth 시작 (App Store 심사 가이드라인 4.8 — Login Services).
 *
 * 🔴 왜 필수인가: 구글·네이버·카카오로 **primary account를 만들 수 있는 앱**은,
 *   ①이름·이메일만 수집 ②계정 설정 시 이메일 비공개 ③동의 없는 광고 추적 없음 —
 *   3개를 모두 만족하는 동등한 로그인 옵션을 함께 제공해야 한다.
 *   3사 모두 ②(이메일 릴레이)가 없어 동등 옵션이 되지 못하므로 SIWA가 유일한 해법이다.
 *   이메일/비밀번호 로그인이 함께 있어도 예외가 아니다(자체 로그인만 쓰는 앱만 예외).
 *
 * 구현은 구글과 동일한 Supabase OAuth 경로다 — 콜백도 같은 redirectTo로 돌아오고
 * deeplink.ts는 provider를 구분하지 않으므로(access_token fragment / PKCE code) 추가 배선이 없다.
 */
export async function startAppleOAuth(): Promise<void> {
  const redirectTo = getOAuthRedirectUri();
  const { data, error } = await supabase.auth.signInWithOAuth({
    provider: "apple",
    options: {
      redirectTo,
      // 구글과 같은 이유 — 자동 리다이렉트 대신 시스템 브라우저로 우리가 연다
      skipBrowserRedirect: true,
    },
  });
  if (error) throw new Error(`Apple 로그인 시작 실패: ${error.message}`);
  if (!data?.url) throw new Error("Apple 로그인 URL을 받지 못했습니다.");

  if (Capacitor.isNativePlatform()) {
    await Browser.open({ url: data.url, presentationStyle: "popover" });
  } else {
    window.location.href = data.url;
  }
}

/**
 * Naver OAuth 시작. 웹의 자체 OAuth 라우트로 위임 (state 쿠키 + code 교환은 서버 처리).
 * from=mobile은 deep link, from=web은 SPA callback으로 token_hash를 반환한다.
 */
export async function startNaverOAuth(): Promise<void> {
  const isNative = Capacitor.isNativePlatform();
  const params = new URLSearchParams({ from: isNative ? "mobile" : "web", next: "/" });
  if (!isNative) params.set("redirect_to", getWebOAuthRedirectUri());
  const url = `${API_BASE}/api/auth/naver/start?${params.toString()}`;
  if (isNative) {
    await Browser.open({ url, presentationStyle: "popover" });
  } else {
    window.location.href = url;
  }
}

/**
 * Kakao OAuth 시작. 웹의 자체 OAuth 라우트로 위임 (state 쿠키 + code 교환은 서버 처리).
 * from=mobile은 deep link, from=web은 SPA callback으로 token_hash를 반환한다.
 */
export async function startKakaoOAuth(): Promise<void> {
  const isNative = Capacitor.isNativePlatform();
  const params = new URLSearchParams({ from: isNative ? "mobile" : "web", next: "/" });
  if (!isNative) params.set("redirect_to", getWebOAuthRedirectUri());
  const url = `${API_BASE}/api/auth/kakao/start?${params.toString()}`;
  if (isNative) {
    await Browser.open({ url, presentationStyle: "popover" });
  } else {
    window.location.href = url;
  }
}

// ── 계정연동(link) — 로그인된 상태에서 현재 계정에 SNS 식별자 추가 (design doc §6) ──
// 로그인과 달리 mode=link로 진입 → callback이 token_hash 대신 link_ticket을 deep link로 반환
// → deeplink.ts가 Bearer와 함께 /api/auth/link/confirm 호출.

/**
 * 네이버/카카오 연동의 발급 바인딩 — 브라우저를 열기 "전" Bearer로 intent를 선발급한다.
 * 서버가 jti↔현재 user_id를 바인딩해 두므로, 시스템 브라우저 callback이 만드는 ticket을
 * 공격자가 가로채 자기 Bearer로 confirm해도 user 불일치로 거부된다(ticket 탈취/재사용 차단).
 * @returns OAuth start URL에 실을 jti
 */
async function issueLinkIntent(provider: "naver" | "kakao"): Promise<string> {
  const { jti } = await apiFetch<{ jti: string }>("/api/auth/link/intent", {
    method: "POST",
    body: { provider },
  });
  if (!jti) throw new Error("연동 준비에 실패했어요. 다시 시도해주세요.");
  return jti;
}

/**
 * 구글 계정연동 시작 (마이페이지 > 계정 연동).
 * 네이버/카카오와 달리 구글은 진짜 Supabase identity라, 커스텀 link_ticket이 아닌
 * Supabase 네이티브 linkIdentity를 쓴다 (대시보드 Manual Linking 활성화 필요 — 적용 완료).
 * 콜백은 로그인과 같은 com.sotda.app://auth/callback로 돌아오므로, 플래그로 연동임을 표시한다.
 */
export async function startGoogleLink(): Promise<void> {
  const redirectTo = getOAuthRedirectUri();
  const { data, error } = await supabase.auth.linkIdentity({
    provider: "google",
    options: {
      redirectTo,
      skipBrowserRedirect: true,
    },
  });
  if (error) throw new Error(`구글 연동 시작 실패: ${error.message}`);
  if (!data?.url) throw new Error("구글 연동 URL을 받지 못했습니다.");

  // deeplink가 로그인 콜백과 구분하도록 "연동 중" 플래그를 남긴다 (소비 시 제거·5분 만료)
  await Preferences.set({
    key: PENDING_ID_LINK_KEY,
    value: JSON.stringify({ provider: "google", ts: Date.now() }),
  });

  if (Capacitor.isNativePlatform()) {
    await Browser.open({ url: data.url, presentationStyle: "popover" });
  } else {
    window.location.href = data.url;
  }
}

/** 네이버 계정연동 시작 (마이페이지 > 계정 연동) */
export async function startNaverLink(): Promise<void> {
  const jti = await issueLinkIntent("naver"); // 발급 바인딩 — 브라우저 열기 전 Bearer로 선발급
  const isNative = Capacitor.isNativePlatform();
  const params = new URLSearchParams({
    from: isNative ? "mobile" : "web", mode: "link", intent: jti, next: "/account-link",
  });
  if (!isNative) params.set("redirect_to", getWebOAuthRedirectUri());
  const url = `${API_BASE}/api/auth/naver/start?${params.toString()}`;
  if (isNative) {
    await Browser.open({ url, presentationStyle: "popover" });
  } else {
    window.location.href = url;
  }
}

/** 카카오 계정연동 시작 (마이페이지 > 계정 연동) */
export async function startKakaoLink(): Promise<void> {
  const jti = await issueLinkIntent("kakao"); // 발급 바인딩 — 브라우저 열기 전 Bearer로 선발급
  const isNative = Capacitor.isNativePlatform();
  const params = new URLSearchParams({
    from: isNative ? "mobile" : "web", mode: "link", intent: jti, next: "/account-link",
  });
  if (!isNative) params.set("redirect_to", getWebOAuthRedirectUri());
  const url = `${API_BASE}/api/auth/kakao/start?${params.toString()}`;
  if (isNative) {
    await Browser.open({ url, presentationStyle: "popover" });
  } else {
    window.location.href = url;
  }
}
